<script lang="ts">
export default {
  name: 'PagingControl',
  components: {}
};
</script>

<script setup lang="ts">
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { nextTick, ref, onBeforeUnmount, onMounted } from 'vue';

const emit = defineEmits<{
  'set-page': [number],
  'previous-page': [],
  'next-page': []
}>();

// PROPS
// ------------------------------------------------
const props = defineProps({
  currentPage: {
    type: Number,
    required: true
  },
  totalPages: {
    type: Number,
    required: true
  }
});

// REFS
// ------------------------------------------------
const pageSelectionOpen = ref(false);
const pageSelector = ref<HTMLInputElement>();

// METHODS
// ------------------------------------------------
function openPageSelector() {
  pageSelectionOpen.value = true;
  nextTick(() => {
    pageSelector.value?.select();
  });
}

function setPage() {
  const page = parseInt(pageSelector.value?.value ?? '');

  if (!isNaN(page) && page >= 1 && page <= props.totalPages) {
    emit('set-page', page);
  }
  
  pageSelectionOpen.value = false;
}

function handleKeyDown(e: KeyboardEvent) {
  if ((e.key === 'Enter' || e.key === 'Escape') && pageSelectionOpen.value) {
    setPage();
  }
}

// LIFECYCLE
// ------------------------------------------------
onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
});
</script>

<template>
  <div class="tw:flex tw:justify-center">
    <div class="tw:join tw:mt-6">
      <button @click="emit('previous-page')" class="tw:join-item tw:btn" :class="{'tw:btn-disabled': currentPage && currentPage === 1}">
        <ChevronLeft class="tw:h-6 tw:w-4" />
      </button>

      <button v-if="!pageSelectionOpen && currentPage !== 0 && totalPages !== 0" @click="openPageSelector"
              class="tw:join-item tw:btn tw:font-medium tw:transition-none">
        Page {{ currentPage }} / {{ totalPages }}
      </button>

      <div v-if="pageSelectionOpen" class="tw:join-item tw:btn tw:font-medium tw:transition-none">
        Page <input ref="pageSelector" type="text" :value="currentPage"
                    class="tw:w-8 tw:text-center tw:rounded-[1px]"
                    @blur="setPage" /> / {{ totalPages }}
      </div>

      <button @click="emit('next-page')" class="tw:join-item tw:btn"
              :class="{'tw:btn-disabled': !totalPages || !currentPage || (currentPage === totalPages)}">
        <ChevronRight class="tw:h-6 tw:w-4" />
      </button>
    </div>
  </div>
</template>
